import { useEffect, useState } from 'react';

/**
 * Re-render on a fixed interval so elapsed-time readouts stay honest.
 *
 * The board's ages come from `minutesSince(order.createdAt)`, which is computed
 * during render — so without something forcing a render, a card keeps showing
 * the age it had when the data last arrived. With `staleTime: 30_000` and the
 * focus/reconnect refetches deliberately off (see useLiveOrders), a quiet board
 * can sit untouched for a long time: a card reads "4m" twenty minutes later,
 * and `timerColor`'s amber-at-11 / red-at-20 thresholds never fire. The ageing
 * clock is the point of the page, so it gets its own heartbeat.
 *
 * Deliberately NOT a refetch — this costs one render a minute and no request.
 *
 * @param intervalMs How often to re-render. Defaults to 30s: ages are shown in
 *   whole minutes, so anything faster is invisible, and anything slower can sit
 *   on the wrong side of a colour threshold for too long.
 */
export function useTicker(intervalMs = 30_000): void {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
}
